import {
  useEffect,
  useState
} from 'react'
import {
  Box,
  Button,
  Skeleton
} from '@chakra-ui/react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import {
  setOpenBlogLink,
  usersSelectors
} from 'store'
import {
  CloseIconTransparent,
  FullScreenIcon,
  FullScreenExitIcon
} from 'assets/icons'
import {
  isMobile,
  isNotMobile
} from 'utils/helpers'

const MotionBox = motion(Box)

export const BlogVideoModal = () => {
  const dispatch = useDispatch()
  const [isLandscape, setIsLandscape] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const [isWiden, setIsWiden] = useState(false)
  const headerHeight = document.querySelector('.header')?.offsetHeight
  const footerHeight = document.querySelector('.notificationBar')?.offsetHeight
  const mobileLandscape = isLandscape && isMobile()

  const {
    isBlogFormOpen,
    openBlogLink
  } = useSelector(usersSelectors)

  const isBlogFormForMobile = () => isBlogFormOpen && !isNotMobile()

  const handleIframeSrc = () => {
    const srcParams = 'modestbranding=1&rel=0&fs=0&autoplay=1&mute=1&playsinline=1'

    return openBlogLink + (openBlogLink?.includes('?') ? '&' : '?') + srcParams
  }

  const handleClose = () => {
    setIsWiden(false)
    dispatch(setOpenBlogLink(''))
    setIsLoaded(false)
  }

  const handleWiden = () => {
    if (isMobile()) {
      if (isBlogFormForMobile() && isWiden) {
        return `calc(100svh - ${headerHeight}px)`
      }

      return isWiden
        ? 'calc(100svh - 100px)'
        : `min(${isLandscape ? 450 : 485}px, 100vw)`
    }

    return isWiden
      ? 'min(100vw, calc((100vh - 103px) * 16 / 9))'
      : 'min(650px, 100vw)'
  }

  const handleHeight = () => {
    if (isMobile()) {
      return isWiden
        ? 'min(88svw, calc(100svw * 16 / 9))'
        : 'min(350px, calc(100svw * 9 / 16), calc(100svh - 100px))'
    }

    return isWiden
      ? 'min(calc(100vw * 9 / 16), calc(100vh - 100px))'
      : 'calc(min(655.5px, 100vw) * 9 / 16)'
  }

  useEffect(() => {
    if (mobileLandscape) {
      setIsWiden(false)
    }
  }, [isLandscape])

  useEffect(() => {
    const onResize = () => {
      const landscape = window.matchMedia('(orientation: landscape)').matches
      setIsLandscape(landscape)
    }

    window.addEventListener('resize', onResize)
    onResize()

    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!openBlogLink) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [openBlogLink])

  return (
    <AnimatePresence>
      {
        openBlogLink
          ? (
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              position="fixed"
              top={0}
              left={0}
              width="100%"
              height="100%"
              zIndex="9998"
              alignItems="center"
              justifyContent="center"
              display="flex"
              onClick={() => handleClose()}
            >
              <Box onClick={(e) => e.stopPropagation()}>
                <Box
                  width="100%"
                  position="fixed"
                  backdropFilter="blur(4px)"
                  background="rgba(250, 250, 250, 0.4)"
                  left={0}
                  top={`${headerHeight - (isBlogFormForMobile() ? 3 : 0)}px`}
                  height={`calc(100svh - ${headerHeight}px - ${isBlogFormForMobile() ? 0 : footerHeight}px)`}
                  onClick={() => handleClose()}
                />

                <Box
                  position="fixed"
                  marginTop={isBlogFormForMobile() ? '22px' : '0'}
                  top="50%"
                  left="50%"
                  transform={`translate(-50%, -50%) ${isMobile() && isWiden ? 'rotate(90deg)' : ''}`}
                  background="white"
                  borderRadius="5px"
                  width={handleWiden()}
                  transition="0.8s ease"
                  maxHeight="calc(100vh - 100px)"
                  boxShadow={false}
                  overflow="visible"
                >
                  {
                    !openBlogLink.includes('https://odysee.com/')
                    && isLoaded
                    && (
                      <Button
                        backgroundColor="transparent"
                        zIndex="30"
                        borderRadius="full"
                        position="absolute"
                        left="8px"
                        top="8px"
                        width="20px"
                        height="20px"
                        padding="0"
                        minWidth="0"
                        boxShadow="0 4px 10px rgba(0, 0, 0, 0.2)"
                        filter="drop-shadow(0 2px 4px rgba(0,0,0,0.6))"
                        onClick={() => {
                          if (!mobileLandscape) {
                            setIsWiden(!isWiden)
                          }
                        }}
                      >
                        { isWiden ? <FullScreenExitIcon /> : <FullScreenIcon /> }
                      </Button>
                    )
                  }

                  <Button
                    backgroundColor="transparent"
                    zIndex="30"
                    borderRadius="full"
                    position="absolute"
                    right="6px"
                    top="6px"
                    width="20px"
                    height="20px"
                    padding="0"
                    minWidth="0"
                    boxShadow="0 4px 10px rgba(0, 0, 0, 0.2)"
                    onClick={() => handleClose()}
                    filter="drop-shadow(0 2px 4px rgba(0,0,0,0.6))"
                  >
                    <CloseIconTransparent />
                  </Button>

                  <Box
                    padding="3.5px"
                    display="flex"
                    justifyContent="center"
                    transition="0.8s ease"
                    height={handleHeight()}
                  >
                    <Box
                      position="relative"
                      overflow="hidden"
                      borderRadius="5px"
                      width="100%"
                      height="100%"
                      isolation="isolate"
                      transform="translateZ(0)"
                    >
                      <Skeleton
                        isLoaded={isLoaded}
                        fadeDuration={1}
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                      >
                        <iframe
                          id="video-iframe"
                          title="video-iframe"
                          width="100%"
                          height="100%"
                          loading="lazy"
                          onLoad={() => setIsLoaded(true)}
                          src={handleIframeSrc()}
                          style={{
                            borderRadius: '5px',
                            border: 0
                          }}
                          allowFullScreen={false}
                          allow="fullscreen 'none'"
                          sandbox="allow-scripts allow-same-origin allow-presentation"
                        />

                        {
                          openBlogLink.includes('https://odysee.com/')
                            && (
                              <Button
                                position="absolute"
                                bottom="0"
                                right="0"
                                zIndex="40"
                                borderRadius="0"
                                width="45px"
                                height="43.5px"
                                padding="0"
                                background="transparent"
                                onClick={() => {
                                  if (!mobileLandscape) {
                                    setIsWiden(!isWiden)
                                  }
                                }}
                                _hover={false}
                                _active={false}
                                _focusVisible={false}
                                _focus={false}
                              >
                                <Box padding={isWiden ? '0 5px 5px 0' : '0 3px 0 0'}>
                                  {
                                    isWiden
                                      ? <FullScreenExitIcon />
                                      : <FullScreenIcon />
                                  }
                                </Box>
                              </Button>
                            )
                        }
                      </Skeleton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </MotionBox>
          ) : ''
      }
    </AnimatePresence>
  )
}
